/**
 * GroveVisitor — something at the treeline, drawn to the fire.
 *
 * Two reflections and nothing else. No body, no art, no sprite: at the edge of
 * firelight an animal *is* a pair of eyes, and everything you think you can see
 * around them you are supplying yourself. That is a better creature than any
 * silhouette this project could generate, and it costs one canvas gradient.
 *
 * Drawn on the unfiltered light camera, like the hearth glow and the lantern,
 * because these are reflections of the fire — the deep-night grade drains
 * almost all colour out of the frame by design, and a warm point that survives
 * that is exactly what reads as catching the light.
 *
 * See visitorRules.ts for when it comes and goes; this file only knows how to
 * show it.
 */
import * as Phaser from 'phaser';
import {
  initialVisitorState,
  stepVisitor,
  visitorDistanceFactor,
  type VisitorState,
} from './visitorRules';

const EYE_TEXTURE = '__grove_eye__';
const EYE_TEX_SIZE = 64;

/** Depth: above the painted planes, below the walker. */
const EYE_DEPTH = 96;

/** Eye separation and size, as fractions of the viewport width. */
const EYE_GAP_FRAC = 0.011;
const EYE_SIZE_FRAC = 0.0075;

/** How much bigger and wider-set it reads once it has come close. */
const NEAR_SCALE = 1.5;

/** Blink timing. Irregular on purpose — a metronome reads as a light, not a face. */
const BLINK_MIN_MS = 2600;
const BLINK_MAX_MS = 7200;
const BLINK_LENGTH_MS = 150;

export interface GroveVisitorOptions {
  /** Where the fire is, in world px. Re-read every frame — the fire is fixed
   *  but the world can resize under it. */
  firePosition: () => { x: number; y: number };
  /** How far the fire's light reaches, in world px. */
  lightRadius: () => number;
  /** Normalised y band the treeline occupies, for where eyes may sit. */
  treelineNY?: [number, number];
  /** Deterministic seed, so a run can be reproduced. */
  seed?: number;
}

export class GroveVisitor {
  private readonly scene: Phaser.Scene;
  private readonly options: Required<GroveVisitorOptions>;
  private readonly left: Phaser.GameObjects.Image;
  private readonly right: Phaser.GameObjects.Image;

  private state: VisitorState = initialVisitorState();
  /** Which side of the fire it chose this time, and how far along the treeline. */
  private side = 1;
  private ny = 0.64;
  private rng: () => number;

  private blinkIn = 4000;
  private blinkFor = 0;
  private destroyed = false;

  constructor(scene: Phaser.Scene, options: GroveVisitorOptions) {
    this.scene = scene;
    this.options = {
      treelineNY: [0.615, 0.665],
      seed: 20260823,
      ...options,
    };
    this.rng = GroveVisitor.mulberry32(this.options.seed);

    GroveVisitor.ensureEyeTexture(scene);
    this.left = this.makeEye();
    this.right = this.makeEye();
    this.pickSpot();
  }

  private makeEye(): Phaser.GameObjects.Image {
    const img = this.scene.add.image(0, 0, EYE_TEXTURE);
    img.setBlendMode(Phaser.BlendModes.ADD);
    img.setOrigin(0.5, 0.5);
    img.setDepth(EYE_DEPTH);
    img.setVisible(false);
    return img;
  }

  /** Objects for camera routing — these are light, and must escape the grade. */
  getDisplayObjects(): Phaser.GameObjects.GameObject[] {
    return [this.left, this.right];
  }

  getStage(): VisitorState['stage'] {
    return this.state.stage;
  }

  getState(): VisitorState {
    return this.state;
  }

  /**
   * @param fuel 0..1 fire fuel.
   * @param darkness 0..1 how much night is in effect.
   * @param delta frame delta in ms.
   */
  update(fuel: number, darkness: number, delta: number): void {
    if (this.destroyed) return;

    const before = this.state.stage;
    this.state = stepVisitor(this.state, { fuel, darkness, deltaMs: delta });

    // A fresh arrival picks a fresh spot, so it is not always the same tree.
    if (before === 'absent' && this.state.stage !== 'absent') this.pickSpot();

    if (this.state.presence <= 0.01) {
      this.left.setVisible(false);
      this.right.setVisible(false);
      return;
    }

    this.tickBlink(delta);
    this.layout();
  }

  private tickBlink(delta: number): void {
    if (this.blinkFor > 0) {
      this.blinkFor -= delta;
      return;
    }
    this.blinkIn -= delta;
    if (this.blinkIn <= 0) {
      this.blinkFor = BLINK_LENGTH_MS;
      this.blinkIn = BLINK_MIN_MS + this.rng() * (BLINK_MAX_MS - BLINK_MIN_MS);
    }
  }

  private pickSpot(): void {
    this.side = this.rng() < 0.5 ? -1 : 1;
    const [a, b] = this.options.treelineNY;
    this.ny = a + this.rng() * (b - a);
  }

  private layout(): void {
    const fire = this.options.firePosition();
    const reach = this.options.lightRadius();
    const w = this.scene.scale.width;
    const h = this.scene.scale.height;

    const dist = reach * visitorDistanceFactor(this.state.stage);
    const x = fire.x + this.side * dist;
    const y = this.ny * h;

    const near = this.state.stage === 'near';
    const scale = near ? NEAR_SCALE : 1;
    const gap = EYE_GAP_FRAC * w * scale;
    const size = EYE_SIZE_FRAC * w * scale;

    // Blinking closes both together, which is what makes two dots a face
    // rather than two lights.
    const blink = this.blinkFor > 0 ? 0.06 : 1;
    // Watching, it is barely there. Near, it is unmistakable — but never
    // bright: this is reflected light, and it has to sit under the fire that
    // is causing it.
    const alpha = this.state.presence * blink * (near ? 0.85 : 0.5);

    for (const [img, dir] of [[this.left, -1], [this.right, 1]] as const) {
      img.setVisible(true);
      img.setPosition(x + dir * gap * 0.5, y);
      img.setAlpha(alpha);
      // Squashed slightly, so they read as eyes rather than as embers.
      const sx = (size * 2) / EYE_TEX_SIZE;
      img.setScale(sx, sx * 0.72);
    }
  }

  destroy(): void {
    if (this.destroyed) return;
    this.destroyed = true;
    this.left.destroy();
    this.right.destroy();
  }

  /** Warm reflective point with a soft edge. */
  private static ensureEyeTexture(scene: Phaser.Scene): void {
    if (scene.textures.exists(EYE_TEXTURE)) return;
    const canvas = document.createElement('canvas');
    canvas.width = EYE_TEX_SIZE;
    canvas.height = EYE_TEX_SIZE;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;
    const r = EYE_TEX_SIZE / 2;
    const g = ctx.createRadialGradient(r, r, 0, r, r, r);
    // Amber rather than white: it is the fire being reflected, so it should be
    // the fire's colour. Zero blue, for the same reason the flame has none —
    // these are ADD-blended and blue accumulates toward cyan.
    g.addColorStop(0.00, 'rgba(255, 214, 150, 0.95)');
    g.addColorStop(0.35, 'rgba(255, 168, 74, 0.55)');
    g.addColorStop(0.72, 'rgba(214, 108, 30, 0.16)');
    g.addColorStop(1.00, 'rgba(160, 70, 20, 0)');
    ctx.fillStyle = g;
    ctx.fillRect(0, 0, EYE_TEX_SIZE, EYE_TEX_SIZE);
    scene.textures.addCanvas(EYE_TEXTURE, canvas);
  }

  /** Small deterministic PRNG, so a seed reproduces a run exactly. */
  private static mulberry32(seed: number): () => number {
    let a = seed >>> 0;
    return () => {
      a |= 0;
      a = (a + 0x6d2b79f5) | 0;
      let t = Math.imul(a ^ (a >>> 15), 1 | a);
      t = (t + Math.imul(t ^ (t >>> 7), 61 | t)) ^ t;
      return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
    };
  }
}
